"use client";

import { useCallback, useState } from "react";
import { useToast } from "@west-tennessee-consulting/ui";
import { generateGoogleAuthUrl } from "@/lib/oauth-utils";

interface UseGoogleDriveReauthOptions {
  isSecondAccount?: boolean;
}

interface UseGoogleDriveReauthResult {
  /** True while the OAuth URL is being generated and the redirect is queued. */
  isReauthing: boolean;
  /**
   * Kicks off the Google Drive re-authentication flow: generates a PKCE OAuth
   * URL with the Drive scopes, flags the cache for a post-OAuth clear, and
   * redirects the browser to Google's consent screen.
   */
  startReauth: () => Promise<void>;
}

/**
 * Shared Google Drive re-authentication action.
 *
 * Single source of truth for "reconnect Google Drive" so every surface that
 * presents a Drive access problem (the browser's "Access Required" empty
 * state, the account status card, settings) triggers the EXACT same remediation
 * flow. See ERROR_STATES_ACTIONABLE rule: an error state must offer an immediate
 * fix, not just describe the problem.
 */
export function useGoogleDriveReauth({
  isSecondAccount = false,
}: UseGoogleDriveReauthOptions = {}): UseGoogleDriveReauthResult {
  const [isReauthing, setIsReauthing] = useState(false);
  const { toast } = useToast();

  const startReauth = useCallback(async () => {
    try {
      setIsReauthing(true);

      const clientId = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID;
      if (!clientId) {
        throw new Error("Google OAuth client ID not configured");
      }

      const redirectUri = `${window.location.origin}/unifiedtime/intermediate/`;
      const authUrl = await generateGoogleAuthUrl(isSecondAccount, clientId, redirectUri);

      if (!authUrl) {
        throw new Error("Failed to generate authentication URL");
      }

      toast({
        title: "Redirecting to Google",
        description: "Please grant Drive access permissions to continue.",
      });

      // Flag so the Drive cache is cleared once OAuth completes.
      if (typeof window !== "undefined") {
        sessionStorage.setItem("clearCacheAfterOAuth", "true");
        // Small delay so the toast renders before navigation.
        setTimeout(() => {
          window.location.href = authUrl;
        }, 100);
      }
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to initiate re-authentication";
      toast({
        title: "Authentication Error",
        description: message,
        variant: "destructive",
      });
      setIsReauthing(false);
    }
  }, [isSecondAccount, toast]);

  return { isReauthing, startReauth };
}
